// web-frontend/src/shared/services/chatsService.js 
// 🔥🔥🔥 2025-12-25 수정: 템플릿 리터럴 문법 오류 전체 수정 - 백틱을 괄호 안에 올바르게 배치
// 🆕🆕🆕 2025-12-31 추가: 설문 리마인더 WebSocket 핸들러 추가
// 🚪🚪🚪 2026-01-07 추가: 사용자 채팅방 퇴장 알림 (user_left_chat) 핸들러 추가
// 🔧🔧🔧 2026-01-09 수정: 템플릿 리터럴 문법 오류 전면 재수정 (백틱 → 괄호 내부 배치)
// 🔥🔥🔥 2026-01-09 추가: sendMessage 함수에 파일 관련 필드 전달 (file_url, file_name, file_size, file_type, thumbnail_url, duration)
import apiClient from './apiClient';
import { API_CONFIG, CHAT_MESSAGE_TYPES } from '../constants';
import { getStoredToken, getStoredUserId, DEV_USER_ID } from '../utils/storage';
import logger from '../utils/logger';

class ChatsService {
  constructor() {
    this.ws = null;
    this.messageHandlers = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.heartbeatInterval = null;
    this.userId = null;
    
    // 번역 관련 속성
    this.translatedMessages = new Map();
    this.preferredLanguage = 'ko';
    this.autoTranslateEnabled = true;
    this.translationHandlers = [];
    
    // 채팅방 업데이트 핸들러
    this.chatRoomHandlers = [];
    
    // 🆕 온라인 상태 핸들러
    this.onlineStatusHandlers = [];
    
    // 🔥🔥🔥 타이핑 인디케이터 핸들러
    this.typingHandlers = [];

    // 🔥🔥🔥 읽음 상태 핸들러
    this.readReceiptHandlers = [];
    
    // 🆕🆕🆕 미팅 상태 업데이트 핸들러
    this.meetingStatusHandlers = [];
    
    // 🆕🆕🆕 설문 리마인더 핸들러
    this.surveyReminderHandlers = [];
    
    // 🔥🔥🔥 [추가] 재연결 핸들러
    this.reconnectionHandlers = [];
    
    // 향후 기능 플래그
    this.features = {
      translation: false,
      fileSharing: false,
      voiceCall: false,
    };
  }

  // =================== 🔥🔥🔥 API 응답 정규화 헬퍼 ===================
  
  normalizeApiResponse(response, context = '') {
    try {
      const data = response?.data || response;
      
      if (!data) {
        logger.error(`❌ [${context}] 응답이 없습니다:`, response);
        return null;
      }
      
      if (Array.isArray(data)) {
        logger.debug(`✅ [${context}] 배열 응답 정규화 완료: ${data.length}개`);
        return data;
      }
      
      if (data.chats && Array.isArray(data.chats)) {
        logger.debug(`✅ [${context}] chats 배열 추출: ${data.chats.length}개`);
        return data.chats;
      }
      
      if (data.messages && Array.isArray(data.messages)) {
        logger.debug(`✅ [${context}] messages 배열 추출: ${data.messages.length}개`);
        return data.messages;
      }
      
      if (typeof data === 'object') {
        logger.debug(`✅ [${context}] 객체 응답 정규화 완료`);
        return data;
      }
      
      logger.debug(`✅ [${context}] 기본 타입 응답: ${typeof data}`);
      return data;
      
    } catch (error) {
      logger.error(`❌ [${context}] 응답 정규화 실패:`, error);
      return null;
    }
  }
  
  normalizeApiError(error, context = '') {
    try {
      const errorData = error.response?.data || error.response || error;
      
      const errorMessage = errorData?.detail || 
                          errorData?.message || 
                          errorData?.error ||
                          error.message || 
                          '알 수 없는 오류가 발생했습니다.';
      
      logger.error(`❌ [${context}] API 에러:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: errorMessage,
        data: errorData
      });
      
      const normalizedError = new Error(errorMessage);
      normalizedError.status = error.response?.status;
      normalizedError.statusText = error.response?.statusText;
      normalizedError.originalError = error;
      
      return normalizedError;
      
    } catch (normalizationError) {
      logger.error(`❌ [${context}] 에러 정규화 실패:`, normalizationError);
      return new Error('에러 처리 중 문제가 발생했습니다.');
    }
  }

  handleApiResponse(response, context = '', allowedErrorStatuses = []) {
    if (response?.isError) {
      const status = response.status;
      
      if (allowedErrorStatuses.includes(status)) {
        logger.debug(`ℹ️ [${context}] 허용된 에러 상태 (${status}) - null 반환`);
        return null;
      }
      
      logger.error(`❌ [${context}] API 에러 응답:`, {
        status: status,
        statusText: response.statusText,
        data: response.data
      });
      
      const error = new Error(
        response.data?.detail || 
        response.data?.message || 
        `요청 처리 중 오류가 발생했습니다 (${status})`
      );
      error.status = status;
      error.response = response;
      throw error;
    }
    
    return response;
  }

  normalizeMessageData(message) {
    if (!message || typeof message !== 'object') {
      logger.warn('⚠️ 유효하지 않은 메시지 객체:', message);
      return null;
    }

    const messageId = message.id || 
                     message.message_id || 
                     message._id || 
                     message.messageId;

    if (!messageId) {
      logger.error('❌ 메시지 ID가 없습니다. 임시 ID 생성:', message);
      const tempId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      message.id = tempId;
    }
    
    const timestamp = message.sent_at || 
                     message.created_at || 
                     message.createdAt || 
                     message.sentAt ||
                     new Date().toISOString();
    
    return {
      ...message,
      sent_at: timestamp,
      created_at: timestamp,
      createdAt: timestamp,
      sentAt: timestamp,
      id: message.id || messageId,
      chat_id: message.chat_id || message.chatId || message.chatRoomId || message.conversation_id,
      sender_id: message.sender_id || message.senderId || message.from || null,
      sender_name: message.sender_name || message.senderName || message.user_name || 'Unknown',
      sender_type: message.sender_type || message.senderType || message.user_type || 'USER',
      message_type: message.message_type || message.messageType || message.type || 'text',
      content: message.content || message.message || message.text || '',
      status: message.status || 'sent',
      is_edited: message.is_edited || message.isEdited || false,
      is_deleted: message.is_deleted || message.isDeleted || false,
      read_by: Array.isArray(message.read_by) ? message.read_by : 
              Array.isArray(message.readBy) ? message.readBy : [],
      reactions: message.reactions || {},
      metadata: message.metadata || message.message_metadata || {},
      message_metadata: message.message_metadata || message.metadata || {},
    };
  }

  normalizeMessages(messages) {
    if (!Array.isArray(messages)) {
      logger.warn('⚠️ 메시지가 배열이 아닙니다:', messages);
      return [];
    }
    
    return messages
      .map(msg => this.normalizeMessageData(msg))
      .filter(msg => msg !== null && msg.id);
  }

  // =================== 번역 관련 설정 ===================
  
  static SUPPORTED_LANGUAGES = [
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  ];

  // =================== 🔥🔥🔥 WebSocket 관련 메서드 (핵심 수정) ===================
  
  async connectWebSocket(userId) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      logger.debug('✅ WebSocket 이미 연결됨');
      return;
    }

    try {
      let storedUserId = null;
      try {
        storedUserId = await getStoredUserId();
      } catch (error) {
        logger.warn('⚠️ 저장된 사용자 ID 조회 실패:', error);
      }
      
      const finalUserId = storedUserId || userId || null;
      
      if (!finalUserId) {
        logger.error('❌ WebSocket 연결 실패: 사용자 ID가 없습니다');
        throw new Error('사용자 ID가 필요합니다.');
      }
      
      this.userId = finalUserId;
    } catch (error) {
      logger.error('❌ 사용자 ID 조회 실패:', error);
      throw new Error('WebSocket 연결에 실패했습니다.');
    }
    
    return new Promise(async (resolve, reject) => {
      try {
        let token = null;
        try {
          token = await getStoredToken();
        } catch (error) {
          logger.warn('⚠️ 토큰 가져오기 실패:', error);
        }
        
        if (!token) {
          token = 'dummy-auth-token'; 
        }
        
        // ============================================================
        // 🔥🔥🔥 [핵심 수정] WebSocket URL 동적 생성 로직
        // ============================================================
        let wsUrl;
        
        // 1. apiClient의 baseURL 확인
        let httpBaseUrl = apiClient.baseURL || 'http://localhost:8000/api';
        
        // 2. 현재 브라우저 프로토콜 확인 (https면 wss 사용 필수)
        const isSecure = window.location.protocol === 'https:';
        const protocol = isSecure ? 'wss://' : 'ws://';
        
        // 3. URL 변환 로직
        if (httpBaseUrl.startsWith('http')) {
            wsUrl = httpBaseUrl.replace(/^http/, 'ws').replace(/^https/, 'wss');
        } else if (httpBaseUrl.startsWith('/')) {
            wsUrl = `${protocol}${window.location.host}${httpBaseUrl}`;
        } else {
            wsUrl = `${protocol}localhost:8000/api`;
        }
        
        // 4. API Endpoint 결합 (/api 중복 방지 로직)
        if (wsUrl.endsWith('/api')) {
            wsUrl = `${wsUrl}/chats/ws/${this.userId}?token=${token}`;
        } else {
            wsUrl = `${wsUrl}/api/chats/ws/${this.userId}?token=${token}`;
        }
        
        logger.debug('🔌 WebSocket 연결 시도 URL:', wsUrl);
        
        this.ws = new WebSocket(wsUrl);
        
        this.ws.onopen = () => {
          logger.debug('✅ WebSocket 연결 성공');
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          resolve();
        };
        
        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleWebSocketMessage(data);
          } catch (error) {
            logger.error('❌ 메시지 파싱 실패:', error);
          }
        };
        
        this.ws.onerror = (error) => {
          logger.error('❌ WebSocket 에러:', error);
        };
        
        this.ws.onclose = (event) => {
          logger.debug('🔌 WebSocket 연결 종료:', event.code, event.reason);
          this.stopHeartbeat();
          
          if (event.code !== 1008 && event.code !== 403) {
            this.attemptReconnect();
          }
        };
        
        setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            logger.warn('⏰ WebSocket 연결 타임아웃');
            this.ws.close();
            resolve();
          }
        }, 5000);
        
      } catch (error) {
        logger.error('❌ WebSocket 연결 실패:', error);
        reject(error);
      }
    });
  }
  
  disconnectWebSocket() {
    logger.debug('🔌 WebSocket 연결 해제...');
    
    this.stopHeartbeat();
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.messageHandlers = [];
    this.chatRoomHandlers = [];
    this.translationHandlers = [];
    this.onlineStatusHandlers = [];
    this.typingHandlers = [];
    this.meetingStatusHandlers = [];
    this.readReceiptHandlers = [];
    this.surveyReminderHandlers = [];
    this.reconnectionHandlers = [];
    this.reconnectAttempts = 0;
    
    logger.debug('✅ WebSocket 완전히 해제됨');
  }
  
  // 중복 등록 방지 헬퍼
  _addHandler(list, handler, label) {
    if (typeof handler !== 'function') return;
    if (list.includes(handler)) return; // 동일 참조 중복 방지
    list.push(handler);
  }

  addMessageHandler(handler) {
    this._addHandler(this.messageHandlers, handler, 'message');
  }

  removeMessageHandler(handler) {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
  }

  addChatRoomUpdateHandler(handler) {
    this._addHandler(this.chatRoomHandlers, handler, 'chatRoom');
  }

  removeChatRoomUpdateHandler(handler) {
    this.chatRoomHandlers = this.chatRoomHandlers.filter(h => h !== handler);
  }
  
  addOnlineStatusHandler(handler) {
    this._addHandler(this.onlineStatusHandlers, handler, 'onlineStatus');
  }

  removeOnlineStatusHandler(handler) {
    this.onlineStatusHandlers = this.onlineStatusHandlers.filter(h => h !== handler);
  }

  addTypingHandler(handler) {
    this._addHandler(this.typingHandlers, handler, 'typing');
  }

  removeTypingHandler(handler) {
    this.typingHandlers = this.typingHandlers.filter(h => h !== handler);
  }

  // =================== 🔥🔥🔥 읽음 상태 핸들러 ===================

  addReadReceiptHandler(handler) {
    this._addHandler(this.readReceiptHandlers, handler, 'readReceipt');
  }

  removeReadReceiptHandler(handler) {
    this.readReceiptHandlers = this.readReceiptHandlers.filter(h => h !== handler);
  }

  // =================== 🆕🆕🆕 미팅 상태 핸들러 ===================
  
  addMeetingStatusHandler(handler) {
    this._addHandler(this.meetingStatusHandlers, handler, 'meetingStatus');
  }

  removeMeetingStatusHandler(handler) {
    this.meetingStatusHandlers = this.meetingStatusHandlers.filter(h => h !== handler);
  }

  // =================== 🆕🆕🆕 설문 리마인더 핸들러 ===================
  
  addSurveyReminderHandler(handler) {
    this._addHandler(this.surveyReminderHandlers, handler, 'surveyReminder');
  }

  removeSurveyReminderHandler(handler) {
    this.surveyReminderHandlers = this.surveyReminderHandlers.filter(h => h !== handler);
  }

  // =================== 🔥🔥🔥 [추가] 재연결 핸들러 ===================
  
  /**
   * WebSocket 재연결 시 호출될 핸들러 추가
   * @param {Function} handler - 재연결 성공 시 호출될 콜백 함수
   */
  addReconnectionHandler(handler) {
    this._addHandler(this.reconnectionHandlers, handler, 'reconnection');
  }

  removeReconnectionHandler(handler) {
    this.reconnectionHandlers = this.reconnectionHandlers.filter(h => h !== handler);
  }
  
  // =================== WebSocket 메시지 핸들러 ===================
  
  handleWebSocketMessage(data) {
    // 🆕 새 채팅방 생성 알림
    if (data.type === 'new_chat_room') {
      logger.debug('🆕 새 채팅방 생성 알림:', data.chat_room);
      
      this.chatRoomHandlers.forEach(handler => {
        try {
          handler({
            type: 'new_chat_room',
            chatRoom: data.chat_room,
            createdBy: data.created_by_name,
            timestamp: data.timestamp
          });
        } catch (error) {
          logger.error('❌ 채팅방 핸들러 에러:', error);
        }
      });
    }
    
    // 🆕 새 메시지 수신
    if (data.type === 'new_message' && data.message) {
      const normalizedMessage = this.normalizeMessageData(data.message);
      
      if (!normalizedMessage) {
        logger.error('❌ 메시지 정규화 실패:', data.message);
        return;
      }
      
      const normalizedData = {
        ...data,
        message: normalizedMessage
      };
      
      logger.debug('✅ WebSocket 메시지 정규화 완료:', normalizedMessage.id);
      
      this.messageHandlers.forEach(handler => {
        try {
          handler(normalizedData);
        } catch (error) {
          logger.error('❌ 메시지 핸들러 에러:', error);
        }
      });
      
      return;
    }

    // 🆕🆕🆕 미팅 상태 업데이트 처리
    if (data.type === 'meeting_status_update') {
      logger.debug('📅 미팅 상태 업데이트 수신:', {
        meetingId: data.meeting_id,
        messageId: data.message_id,
        chatId: data.chat_id,
        newStatus: data.new_status,
        updatedBy: data.updated_by
      });
      
      const statusUpdateData = {
        type: 'meeting_status_update',
        meeting_id: data.meeting_id,
        message_id: data.message_id,
        chat_id: data.chat_id,
        new_status: data.new_status,
        updated_by: data.updated_by,
        timestamp: data.timestamp || new Date().toISOString()
      };
      
      this.meetingStatusHandlers.forEach(handler => {
        try {
          handler(statusUpdateData);
        } catch (error) {
          logger.error('❌ 미팅 상태 핸들러 에러:', error);
        }
      });
      
      return;
    }

    // 🆕🆕🆕 설문 리마인더 알림 처리
    if (data.type === 'read_receipt_update') {
      logger.debug('읽음 상태 업데이트 수신:', {
        chatId: data.chat_id,
        readerUserId: data.reader_user_id,
        readMessageCount: data.read_message_ids?.length || 0,
      });

      this.readReceiptHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          logger.error('읽음 상태 핸들러 오류:', error);
        }
      });

      return;
    }

    if (data.type === 'survey_reminder') {
      logger.debug('📋 설문 리마인더 수신:', {
        surveyId: data.survey_id,
        surveySlug: data.survey_slug,
        meetingId: data.meeting_id,
        meetingTitle: data.meeting_title,
        rewardPoints: data.reward_points
      });
      
      const surveyReminderData = {
        type: 'survey_reminder',
        notification_id: data.notification_id,
        survey_id: data.survey_id,
        survey_slug: data.survey_slug,
        meeting_id: data.meeting_id,
        meeting_title: data.meeting_title,
        title: data.title || '📋 상담 결과를 입력해주세요',
        message: data.message,
        reward_points: data.reward_points || 0,
        action_url: data.action_url,
        priority: data.priority || 'high',
        timestamp: data.timestamp || new Date().toISOString()
      };
      
      this.surveyReminderHandlers.forEach(handler => {
        try {
          handler(surveyReminderData);
        } catch (error) {
          logger.error('❌ 설문 리마인더 핸들러 에러:', error);
        }
      });
      
      return;
    }

    // 타이핑 인디케이터
    if (data.type === 'typing_indicator') {
      logger.debug('⌨️ 타이핑 이벤트 수신:', {
        chatId: data.chat_id,
        userId: data.user_id,
        userName: data.user_name,
        isTyping: data.is_typing
      });
      
      this.typingHandlers.forEach(handler => {
        try {
          handler({
            type: 'typing_indicator',
            chatId: data.chat_id,
            userId: data.user_id,
            userName: data.user_name,
            isTyping: data.is_typing,
            timestamp: data.timestamp || new Date().toISOString()
          });
        } catch (error) {
          logger.error('❌ 타이핑 핸들러 에러:', error);
        }
      });
      
      return;
    }
    
    // 🚪🚪🚪 [추가] 사용자 채팅방 퇴장 알림
    if (data.type === 'user_left_chat') {
      logger.debug('🚪 상대방 채팅방 퇴장 알림:', {
        chatId: data.chat_id,
        userId: data.user_id,
        userName: data.user_name,
        message: data.message
      });
      
      // chatRoomHandlers를 통해 이벤트 전파
      this.chatRoomHandlers.forEach(handler => {
        try {
          handler({
            type: 'user_left_chat',
            chatId: data.chat_id,
            userId: data.user_id,
            userName: data.user_name,
            message: data.message,
            timestamp: data.timestamp || new Date().toISOString()
          });
        } catch (error) {
          logger.error('❌ 채팅방 퇴장 핸들러 에러:', error);
        }
      });
      
      // messageHandlers에도 시스템 메시지로 전달 (채팅 화면에 표시용)
      const systemMessage = {
        type: 'new_message',
        message: {
          id: `system-left-${Date.now()}`,
          chat_id: data.chat_id,
          sender_id: null,
          sender_name: 'System',
          sender_type: 'system',
          content: data.message || `${data.user_name}님이 채팅방을 나갔습니다.`,
          message_type: 'system',
          status: 'sent',
          sent_at: data.timestamp || new Date().toISOString(),
          created_at: data.timestamp || new Date().toISOString(),
          is_system: true,
          metadata: {
            event_type: 'user_left',
            left_user_id: data.user_id,
            left_user_name: data.user_name
          }
        }
      };
      
      this.messageHandlers.forEach(handler => {
        try {
          handler(systemMessage);
        } catch (error) {
          logger.error('❌ 시스템 메시지 핸들러 에러:', error);
        }
      });
      
      return;
    }
    
    // 온라인 상태 응답
    if (data.type === 'online_status_response' && data.statuses) {
      logger.debug('👥 온라인 상태 응답 수신:', data.statuses);
      
      this.onlineStatusHandlers.forEach(handler => {
        try {
          handler(data.statuses);
        } catch (error) {
          logger.error('❌ 온라인 상태 핸들러 에러:', error);
        }
      });
      
      return;
    }
    
    // 사용자 온라인/오프라인 상태 변경
    if (data.type === 'user_online' || data.type === 'user_offline') {
      logger.debug(`👤 사용자 상태 변경: ${data.userId} → ${data.type === 'user_online' ? '온라인' : '오프라인'}`);
      
      const status = {
        [data.userId]: data.type === 'user_online'
      };
      
      this.onlineStatusHandlers.forEach(handler => {
        try {
          handler(status);
        } catch (error) {
          logger.error('❌ 온라인 상태 핸들러 에러:', error);
        }
      });
      
      return;
    }
    
    // 번역 기능 (활성화된 경우)
    if (this.features.translation && this.autoTranslateEnabled && 
        data.type === 'message' && data.content) {
      const messageLanguage = data.language || 'auto';
      if (messageLanguage !== this.preferredLanguage) {
        this.translateMessage(data.content, this.preferredLanguage, messageLanguage)
          .then(translatedContent => {
            const translatedData = {
              ...data,
              originalContent: data.content,
              content: translatedContent,
              isTranslated: true,
              originalLanguage: messageLanguage,
              translatedLanguage: this.preferredLanguage
            };
            
            this.translationHandlers.forEach(handler => {
              try {
                handler(translatedData);
              } catch (error) {
                logger.error('❌ 번역 핸들러 에러:', error);
              }
            });
          })
          .catch(error => {
            logger.error('❌ 메시지 번역 실패:', error);
          });
      }
    }
    
    // 기본 메시지 핸들러 호출 (위에서 처리되지 않은 모든 메시지)
    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        logger.error('❌ 메시지 핸들러 에러:', error);
      }
    });
  }
  
  // =================== 🔥🔥🔥 [핵심 수정] attemptReconnect - 재연결 성공 시 이벤트 발송 ===================
  
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.debug('❌ 최대 재연결 시도 횟수 초과');
      return;
    }

    // 백그라운드 탭이면 재연결 보류 — 탭 활성화 시 재시도
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      logger.debug('⏸️ 백그라운드 탭 — 재연결 보류');
      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          document.removeEventListener('visibilitychange', onVisible);
          this.attemptReconnect();
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      return;
    }
    
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    
    logger.debug(`🔄 ${delay}ms 후 재연결 시도... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    this.reconnectTimeout = setTimeout(async () => {
      if (this.userId) {
        try {
          await this.connectWebSocket(this.userId);
          
          // 🔥🔥🔥 [핵심 수정] 재연결 성공 시 핸들러 호출
          logger.debug('✅ WebSocket 재연결 성공 - 핸들러 호출');
          
          // 1. chatRoomHandlers에 reconnected 이벤트 전송
          this.chatRoomHandlers.forEach(handler => {
            try {
              handler({
                type: 'reconnected',
                timestamp: new Date().toISOString()
              });
            } catch (error) {
              logger.error('❌ 재연결 알림 핸들러 에러:', error);
            }
          });
          
          // 2. 전용 reconnectionHandlers 호출
          this.reconnectionHandlers.forEach(handler => {
            try {
              handler({
                type: 'reconnected',
                timestamp: new Date().toISOString(),
                reconnectAttempts: this.reconnectAttempts
              });
            } catch (error) {
              logger.error('❌ 재연결 핸들러 에러:', error);
            }
          });
          
        } catch (error) {
          logger.error('❌ 재연결 실패:', error);
        }
      }
    }, delay);
  }
  
  startHeartbeat() {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }
  
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
  
  sendWebSocketMessage(message) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logger.warn('⚠️ WebSocket 연결되지 않음');
      return false;
    }
    
    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      logger.error('❌ WebSocket 메시지 전송 실패:', error);
      return false;
    }
  }
  
  // =================== 실시간 번역 기능 (향후 구현) ===================
  
  async translateMessage(text, targetLang, sourceLang = 'auto') {
    if (!this.features.translation) {
      logger.debug('ℹ️ 번역 기능이 비활성화되어 있습니다');
      return text;
    }
    
    try {
      const cacheKey = `${text}_${sourceLang}_${targetLang}`;
      if (this.translatedMessages.has(cacheKey)) {
        logger.debug('💾 번역 캐시 사용');
        return this.translatedMessages.get(cacheKey);
      }
      
      const response = await apiClient.post('/translate', {
        text,
        target: targetLang,
        source: sourceLang
      });
      
      const translatedText = response.data?.translatedText || text;
      
      this.translatedMessages.set(cacheKey, translatedText);
      
      logger.debug(`🌐 번역 완료: ${sourceLang} → ${targetLang}`);
      return translatedText;
      
    } catch (error) {
      logger.error('❌ 번역 실패:', error);
      return text;
    }
  }
  
  async translateChatMessages(chatId, messages, targetLang) {
    if (!this.features.translation) {
      logger.debug('ℹ️ 번역 기능이 비활성화되어 있습니다');
      return messages;
    }
    
    try {
      const translationPromises = messages.map(async (message) => {
        if (message.type === 'text' && message.content) {
          const translatedContent = await this.translateMessage(
            message.content,
            targetLang,
            message.language || 'auto'
          );
          
          return {
            ...message,
            originalContent: message.content,
            content: translatedContent,
            isTranslated: true,
            originalLanguage: message.language || 'auto',
            translatedLanguage: targetLang
          };
        }
        return message;
      });
      
      const translatedMessages = await Promise.all(translationPromises);
      logger.debug(`✅ ${chatId} 채팅방 메시지 ${translatedMessages.length}개 번역 완료`);
      
      return translatedMessages;
    } catch (error) {
      logger.error('❌ 채팅방 메시지 번역 실패:', error);
      return messages;
    }
  }
  
  addTranslationHandler(handler) {
    this._addHandler(this.translationHandlers, handler, 'translation');
  }

  removeTranslationHandler(handler) {
    this.translationHandlers = this.translationHandlers.filter(h => h !== handler);
  }
  
  setPreferredLanguage(language) {
    this.preferredLanguage = language;
    logger.debug(`🌍 선호 언어 설정: ${language}`);
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendWebSocketMessage({
        type: 'language_preference',
        language: language
      });
    }
  }
  
  setAutoTranslate(enabled) {
    this.autoTranslateEnabled = enabled;
    logger.debug(`🔄 자동 번역: ${enabled ? '활성화' : '비활성화'}`);
  }
  
  async sendMultilingualMessage(chatId, content, targetLanguages = ['en', 'zh', 'ja']) {
    if (!this.features.translation) {
      logger.debug('ℹ️ 번역 기능이 비활성화되어 있습니다');
      return await this.sendTextMessage(chatId, content);
    }
    
    try {
      logger.debug(`🌏 다국어 메시지 전송: ${targetLanguages.length}개 언어`);
      
      const originalMessage = await this.sendTextMessage(chatId, content);
      
      const translations = {};
      for (const lang of targetLanguages) {
        if (lang !== this.preferredLanguage) {
          translations[lang] = await this.translateMessage(content, lang, this.preferredLanguage);
        }
      }
      
      if (Object.keys(translations).length > 0) {
        this.sendWebSocketMessage({
          type: 'translated_message',
          chat_id: chatId,
          message_id: originalMessage.id,
          translations: translations,
          original_language: this.preferredLanguage
        });
      }
      
      logger.debug(`✅ 다국어 메시지 전송 완료`);
      
      return {
        original: originalMessage,
        translations: translations
      };
      
    } catch (error) {
      logger.error('❌ 다국어 메시지 전송 실패:', error);
      throw error;
    }
  }
  
  sendTranslatingStatus(chatId, isTranslating = true) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendWebSocketMessage({
        type: 'translating_status',
        chat_id: chatId,
        is_translating: isTranslating,
        user_id: this.userId
      });
    }
  }
  
  getSupportedLanguages() {
    return ChatsService.SUPPORTED_LANGUAGES;
  }
  
  async detectLanguage(text) {
    try {
      const response = await apiClient.post('/detect-language', { text });
      return response.data?.language || 'auto';
    } catch (error) {
      logger.error('❌ 언어 감지 실패:', error);
      return 'auto';
    }
  }
  
  clearTranslationCache() {
    this.translatedMessages.clear();
    logger.debug('🗑️ 번역 캐시 초기화 완료');
  }
  
  // =================== HTTP API 메서드들 ===================
  
  async getChatRooms(params = {}) {
    try {
      const {
        page = 1,
        limit = 20,
        type = '',
        search = '',
        unreadOnly = false,
        sortBy = 'lastMessageAt',
        sortOrder = 'desc',
        ...filters
      } = params;

      const queryParams = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...filters,
      };

      if (type) queryParams.type = type;
      if (search) queryParams.search = search;
      if (unreadOnly) queryParams.unread_only = unreadOnly;

      const response = await apiClient.get('/chats', {
        params: queryParams,
      });

      const data = this.normalizeApiResponse(response, 'getChatRooms');
      
      if (!data) {
        logger.warn('⚠️ 채팅방 목록이 비어있습니다');
        return [];
      }

      const chatsList = Array.isArray(data) ? data : (data.chats || []);
      
      logger.debug(`✅ 채팅방 목록 조회 완료: ${chatsList.length}개`);
      
      const chatRoomsData = chatsList.map(chat => {
        const chatType = chat.chat_type || chat.room_type || 'direct';
        let normalizedRoomType = chatType;
        
        if (chatType === 'one_to_one') {
          normalizedRoomType = 'direct';
        }
        
        return {
          id: chat.id,
          name: chat.name || chat.room_name || 'Unknown Chat',
          room_name: chat.name || chat.room_name || 'Unknown Chat',
          room_type: normalizedRoomType,
          chat_type: chatType,
          participants: chat.participants || [],
          participant_count: chat.participant_count || chat.participants?.length || 0,
          last_message: chat.last_message || null,
          last_activity: chat.last_activity || chat.updated_at || chat.created_at,
          unread_count: chat.unread_count || 0,
          is_pinned: chat.is_pinned || false,
          is_muted: chat.is_muted || false,
          created_at: chat.created_at,
          updated_at: chat.updated_at,
          metadata: chat.metadata || {}
        };
      });
      
      logger.debug(`📊 정규화 완료: ${chatRoomsData.length}개 채팅방`);
      
      return chatRoomsData;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'getChatRooms');
      throw normalizedError;
    }
  }

  async getChatRoomDetails(chatId) {
    try {
      logger.debug('🔍 채팅방 상세 조회 API 호출:', chatId);
      
      const response = await apiClient.get(`/chats/${chatId}`);
      
      // 🔥🔥🔥 [추가] 응답 구조 상세 로깅
      logger.debug('📦 채팅방 상세 응답 raw:', response);
      logger.debug('📦 채팅방 상세 응답 data:', response?.data);
      
      const normalizedData = this.normalizeApiResponse(response, 'getChatRoomDetails');
      
      if (!normalizedData) {
        throw new Error('채팅방 정보를 가져올 수 없습니다.');
      }
      
      // 🔥🔥🔥 [추가] participants 상세 정보 로깅
      if (normalizedData.participants && normalizedData.participants.length > 0) {
        logger.debug('👥 참가자 상세 정보:', normalizedData.participants.map(p => ({
          user_id: p.user_id,
          user_name: p.user_name,
          company_name: p.company_name,
          bio: p.bio,
          job_title: p.job_title,
          booth_number: p.booth_number
        })));
      }
      
      logger.debug('✅ 채팅방 상세 조회 완료:', normalizedData.id);
      
      return normalizedData;
    } catch (error) {
      logger.error('❌ 채팅방 상세 조회 실패:', error);
      
      if (error.response?.status === 404) {
        logger.debug('💡 채팅방 목록에서 검색 시도');
        try {
          const chatRooms = await this.getChatRooms();
          const targetRoom = chatRooms.find(room => room.id === chatId);
          if (targetRoom) {
            logger.debug('✅ 채팅방 목록에서 발견:', targetRoom.id);
            return targetRoom;
          }
        } catch (listError) {
          logger.error('❌ 채팅방 목록 조회도 실패:', listError);
        }
      }
      
      throw this.normalizeApiError(error, 'getChatRoomDetails');
    }
  }

  async createChatRoom(chatData) {
    try {
      if (!chatData.participant_ids || !Array.isArray(chatData.participant_ids) || chatData.participant_ids.length < 2) {
        throw new Error('참여자 정보가 올바르지 않습니다. 최소 2명의 참여자가 필요합니다.');
      }
      
      logger.debug('🔥 채팅방 생성 요청:', chatData);
      
      const requestData = {
        participant_ids: chatData.participant_ids.filter(id => id != null),
        room_type: chatData.room_type || 'direct',
        room_name: chatData.room_name || 'New Chat',
        metadata: chatData.metadata || {}
      };
      
      if (requestData.participant_ids.length < 2) {
        throw new Error('유효한 참여자가 2명 미만입니다.');
      }
      
      logger.debug('📤 API 요청 데이터:', requestData);
      
      let lastError;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const response = await apiClient.post('/chats', requestData);
          
          const responseData = this.normalizeApiResponse(response, 'createChatRoom');
          
          if (!responseData) {
            throw new Error('서버 응답이 유효하지 않습니다.');
          }
          
          logger.debug('✅ 채팅방 생성 성공:', responseData);
          
          if (!responseData.participants || responseData.participants.length === 0) {
            logger.warn('⚠️ 응답에 participants가 없습니다. 재조회 시도...');
            
            try {
              const chatDetailResponse = await apiClient.get(`/chats/${responseData.id}`);
              const chatDetail = this.normalizeApiResponse(chatDetailResponse, 'createChatRoom-detail');
              
              if (chatDetail && chatDetail.participants) {
                responseData.participants = chatDetail.participants;
                logger.debug(`✅ participants 재조회 성공: ${chatDetail.participants.length}명`);
              } else {
                logger.warn('⚠️ 재조회에도 participants가 없음');
                responseData.participants = [
                  { 
                    user_id: requestData.participant_ids[0], 
                    user_name: 'You',
                    user_type: 'user',
                    role: 'admin'
                  },
                  { 
                    user_id: requestData.participant_ids[1], 
                    user_name: chatData.room_name || 'Unknown',
                    user_type: 'company',
                    role: 'member'
                  }
                ];
              }
            } catch (detailError) {
              logger.warn('⚠️ 채팅방 정보 재조회 실패:', detailError);
              responseData.participants = [
                { 
                  user_id: requestData.participant_ids[0], 
                  user_name: 'You',
                  user_type: 'user',
                  role: 'admin'
                },
                { 
                  user_id: requestData.participant_ids[1], 
                  user_name: chatData.room_name || 'Unknown',
                  user_type: 'company',
                  role: 'member'
                }
              ];
            }
          }
          
          const chatRoom = {
            id: responseData.id || responseData.chat_id,
            room_name: responseData.name || responseData.room_name || chatData.room_name,
            room_type: responseData.chat_type || responseData.room_type || chatData.room_type,
            participants: responseData.participants || [],
            participant_count: responseData.participants?.length || 0,
            created_at: responseData.created_at || new Date().toISOString(),
            last_message: null,
            unread_count: 0,
            metadata: responseData.metadata || chatData.metadata || {}
          };
          
          logger.debug(`✅ 최종 채팅방 데이터:`, chatRoom);
          
          return chatRoom;
        } catch (error) {
          lastError = error;
          logger.warn(`⚠️ 채팅방 생성 시도 ${attempt}/3 실패:`, error.message);
          
          const shouldRetry = 
            error.response?.status >= 500 || 
            error.code === 'ECONNABORTED' ||
            error.message.includes('timeout');
          
          if (attempt < 3 && shouldRetry) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          } else if (!shouldRetry) {
            throw error;
          }
        }
      }
      
      throw lastError;
      
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'createChatRoom');
      throw normalizedError;
    }
  }

  async findChatByParticipants(participantIds) {
    try {
      if (!Array.isArray(participantIds) || participantIds.length < 2) {
        throw new Error('최소 2명의 참가자가 필요합니다.');
      }
      
      logger.debug(`🔍 참가자 기반 채팅방 검색: ${participantIds.length}명`, participantIds);
      
      const response = await apiClient.post('/chats/find-by-participants', {
        participant_ids: participantIds
      });
      
      if (response?.isError && response?.status === 404) {
        logger.debug('ℹ️ 일치하는 채팅방 없음 (404)');
        return null;
      }
      
      if (response?.isError) {
        const error = new Error(
          response.data?.detail || 
          response.data?.message || 
          `채팅방 검색 중 오류가 발생했습니다 (${response.status})`
        );
        error.status = response.status;
        error.response = response;
        throw error;
      }
      
      const chatData = this.normalizeApiResponse(response, 'findChatByParticipants');
      
      if (chatData && chatData.id) {
        logger.debug(`✅ 기존 채팅방 발견: ${chatData.id}`);
        return chatData;
      }
      
      logger.debug('ℹ️ 일치하는 채팅방 없음 (응답은 있으나 ID 없음)');
      return null;
      
    } catch (error) {
      logger.error('❌ findChatByParticipants 에러:', error);
      const normalizedError = this.normalizeApiError(error, 'findChatByParticipants');
      throw normalizedError;
    }
  }

  async getChatMessages(chatId, params = {}) {
    try {
      const response = await apiClient.get(`/chats/${chatId}/messages`, { params });
      
      const data = this.normalizeApiResponse(response, 'getChatMessages');
      
      if (!data) {
        logger.warn(`⚠️ 채팅방 ${chatId} 메시지가 없습니다`);
        return [];
      }
      
      const messagesData = Array.isArray(data) ? data : [];
      
      const normalizedMessages = this.normalizeMessages(messagesData);
      
      logger.debug(`✅ 메시지 조회 완료: ${normalizedMessages.length}개 (정규화 적용)`);
      
      return normalizedMessages;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'getChatMessages');
      throw normalizedError;
    }
  }

  // 🔥🔥🔥 [2026-01-09 수정] sendMessage 함수에 파일 관련 필드 전달 추가
  async sendMessage(chatId, messageData) {
    try {
      const enhancedMessageData = {
        content: messageData.content,
        message_type: messageData.message_type || messageData.type || CHAT_MESSAGE_TYPES?.TEXT || 'text',
        language: messageData.language || this.preferredLanguage,
        metadata: {
          ...messageData.metadata,
          autoTranslate: this.autoTranslateEnabled && this.features.translation
        }
      };
      
      // 🔥🔥🔥 [2026-01-09 추가] 파일 관련 필드 전달 (음성/비디오/파일 메시지용)
      if (messageData.file_url) {
        enhancedMessageData.file_url = messageData.file_url;
      }
      if (messageData.file_name) {
        enhancedMessageData.file_name = messageData.file_name;
      }
      if (messageData.file_size) {
        enhancedMessageData.file_size = messageData.file_size;
      }
      if (messageData.file_type) {
        enhancedMessageData.file_type = messageData.file_type;
      }
      if (messageData.thumbnail_url) {
        enhancedMessageData.thumbnail_url = messageData.thumbnail_url;
      }
      if (messageData.duration !== undefined) {
        // duration을 metadata에 포함
        enhancedMessageData.metadata = {
          ...enhancedMessageData.metadata,
          duration: messageData.duration
        };
      }
      
      logger.debug(`📤 메시지 전송 시도: ${chatId}`, enhancedMessageData);
      
      let lastError;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const response = await apiClient.post(`/chats/${chatId}/messages`, enhancedMessageData);
          
          const msgData = this.normalizeApiResponse(response, 'sendMessage');
          
          if (!msgData) {
            throw new Error('서버 응답이 유효하지 않습니다.');
          }
          
          if (!msgData.id) {
            logger.error('❌ 응답에 메시지 ID가 없음:', msgData);
            throw new Error('메시지 ID가 없습니다.');
          }
          
          logger.debug('✅ 메시지 전송 성공 - 원본 응답:', msgData);
          
          const normalizedMessage = this.normalizeMessageData(msgData);
          
          if (!normalizedMessage) {
            logger.error('❌ 메시지 정규화 실패:', msgData);
            throw new Error('메시지 정규화에 실패했습니다.');
          }
          
          if (!normalizedMessage.id) {
            logger.error('❌ 정규화된 메시지에 ID가 없습니다:', normalizedMessage);
            throw new Error('메시지 ID가 없습니다.');
          }
          
          logger.debug('✅ 메시지 정규화 완료:', {
            id: normalizedMessage.id,
            chat_id: normalizedMessage.chat_id,
            content: normalizedMessage.content?.substring(0, 50),
            status: normalizedMessage.status
          });
          
          if (this.features.translation && this.autoTranslateEnabled && normalizedMessage) {
            try {
              const roomLanguages = await this.getChatRoomLanguages(chatId);
              if (roomLanguages && roomLanguages.length > 0) {
                this.sendWebSocketMessage({
                  type: 'request_translation',
                  message_id: normalizedMessage.id,
                  target_languages: roomLanguages,
                  source_language: this.preferredLanguage
                });
              }
            } catch (langError) {
              logger.debug('언어 목록 조회 실패 (무시 가능):', langError.message);
            }
          }
          
          return normalizedMessage;
        } catch (error) {
          lastError = error;
          logger.warn(`⚠️ 메시지 전송 시도 ${attempt}/3 실패:`, error.message);
          
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 500 * attempt));
          }
        }
      }
      
      throw lastError;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'sendMessage');
      throw normalizedError;
    }
  }

  async sendTextMessage(chatId, content, language = null) {
    return this.sendMessage(chatId, {
      type: CHAT_MESSAGE_TYPES?.TEXT || 'text',
      content,
      language: language || this.preferredLanguage
    });
  }

  async sendImageMessage(chatId, imageFile, caption = '') {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('type', CHAT_MESSAGE_TYPES?.IMAGE || 'image');
      if (caption) formData.append('caption', caption);

      const response = await apiClient.post(`/chats/${chatId}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const msgData = this.normalizeApiResponse(response, 'sendImageMessage');
      
      if (!msgData) {
        throw new Error('이미지 메시지 전송 응답이 유효하지 않습니다.');
      }
      
      logger.debug('✅ 이미지 메시지 전송 성공');
      
      return this.normalizeMessageData(msgData);
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'sendImageMessage');
      throw normalizedError;
    }
  }

  async sendFileMessage(chatId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', CHAT_MESSAGE_TYPES?.FILE || 'file');

      const response = await apiClient.post(`/chats/${chatId}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const msgData = this.normalizeApiResponse(response, 'sendFileMessage');
      
      if (!msgData) {
        throw new Error('파일 메시지 전송 응답이 유효하지 않습니다.');
      }
      
      logger.debug('✅ 파일 메시지 전송 성공');
      
      return this.normalizeMessageData(msgData);
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'sendFileMessage');
      throw normalizedError;
    }
  }

  async deleteMessage(chatId, messageId) {
    try {
      await apiClient.delete(`/chats/${chatId}/messages/${messageId}`);
      logger.debug(`✅ 메시지 삭제 완료: ${messageId}`);
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'deleteMessage');
      throw normalizedError;
    }
  }

  async editMessage(chatId, messageId, newContent) {
    try {
      const response = await apiClient.put(`/chats/${chatId}/messages/${messageId}`, {
        content: newContent,
      });
      
      const msgData = this.normalizeApiResponse(response, 'editMessage');
      
      if (!msgData) {
        throw new Error('메시지 수정 응답이 유효하지 않습니다.');
      }
      
      logger.debug(`✅ 메시지 수정 완료: ${messageId}`);
      
      return this.normalizeMessageData(msgData);
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'editMessage');
      throw normalizedError;
    }
  }

  async markAsRead(chatId, messageId) {
    try {
      const endpoint = messageId
        ? `/chats/${chatId}/read?message_id=${encodeURIComponent(messageId)}`
        : `/chats/${chatId}/read`;
      await apiClient.post(endpoint);
      logger.debug(`✅ 읽음 처리 완료: ${chatId}/${messageId}`);
    } catch (error) {
      logger.warn('⚠️ 읽음 처리 실패:', error);
    }
  }

  async markChatAsRead(chatId) {
    try {
      await apiClient.post(`/chats/${chatId}/mark-read`);
      logger.debug(`✅ 채팅방 읽음 처리 완료: ${chatId}`);
    } catch (error) {
      logger.warn('⚠️ 채팅방 읽음 처리 실패:', error);
    }
  }

  async getChatParticipants(chatId) {
    try {
      const response = await apiClient.get(`/chats/${chatId}/participants`);
      const data = this.normalizeApiResponse(response, 'getChatParticipants');
      return data || [];
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'getChatParticipants');
      throw normalizedError;
    }
  }

  async addParticipants(chatId, userIds) {
    try {
      const response = await apiClient.post(`/chats/${chatId}/participants`, {
        userIds,
      });
      logger.debug(`✅ 참여자 추가 완료: ${userIds.length}명`);
      const data = this.normalizeApiResponse(response, 'addParticipants');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'addParticipants');
      throw normalizedError;
    }
  }

  async removeParticipant(chatId, userId) {
    try {
      await apiClient.delete(`/chats/${chatId}/participants/${userId}`);
      logger.debug(`✅ 참여자 제거 완료: ${userId}`);
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'removeParticipant');
      throw normalizedError;
    }
  }

  async leaveChat(chatId) {
    try {
      await apiClient.delete(`/chats/${chatId}`);
      logger.debug(`✅ 채팅방 나가기 완료: ${chatId}`);
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'leaveChat');
      throw normalizedError;
    }
  }

  async updateChatRoom(chatId, updateData) {
    try {
      const response = await apiClient.put(`/chats/${chatId}`, updateData);
      logger.debug(`✅ 채팅방 정보 업데이트 완료: ${chatId}`);
      const data = this.normalizeApiResponse(response, 'updateChatRoom');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'updateChatRoom');
      throw normalizedError;
    }
  }

  async deleteChat(chatId) {
    try {
      await apiClient.delete(`/chats/${chatId}`);
      logger.debug(`✅ 채팅방 삭제 완료: ${chatId}`);
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'deleteChat');
      throw normalizedError;
    }
  }

  async muteChatRoom(chatId, muted = true) {
    try {
      const response = await apiClient.post(`/chats/${chatId}/mute`, {
        muted,
      });
      logger.debug(`✅ 채팅방 음소거 설정: ${muted}`);
      const data = this.normalizeApiResponse(response, 'muteChatRoom');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'muteChatRoom');
      throw normalizedError;
    }
  }

  async pinChatRoom(chatId, pinned = true) {
    try {
      const response = await apiClient.post(`/chats/${chatId}/pin`, {
        pinned,
      });
      logger.debug(`✅ 채팅방 고정 설정: ${pinned}`);
      const data = this.normalizeApiResponse(response, 'pinChatRoom');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'pinChatRoom');
      throw normalizedError;
    }
  }

  async searchChats(searchParams) {
    try {
      const response = await apiClient.get('/chats/search', {
        params: searchParams,
      });
      const data = this.normalizeApiResponse(response, 'searchChats');
      const results = Array.isArray(data) ? data : [];
      logger.debug(`✅ 채팅 검색 완료: ${results.length}개`);
      return results;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'searchChats');
      throw normalizedError;
    }
  }

  async searchMessages(chatId = null, searchParams = {}) {
    try {
      const endpoint = chatId 
        ? `/chats/${chatId}/search` 
        : '/chats/messages/search';
      
      const response = await apiClient.get(endpoint, {
        params: searchParams,
      });
      
      const data = this.normalizeApiResponse(response, 'searchMessages');
      
      if (Array.isArray(data)) {
        const normalizedResults = this.normalizeMessages(data);
        logger.debug(`✅ 메시지 검색 완료: ${normalizedResults.length}개`);
        return normalizedResults;
      }
      
      return data || [];
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'searchMessages');
      throw normalizedError;
    }
  }

  async getUnreadCount() {
    try {
      const response = await apiClient.get('/chats/unread-count');
      const data = this.normalizeApiResponse(response, 'getUnreadCount');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'getUnreadCount');
      throw normalizedError;
    }
  }

  async setTypingStatus(chatId, isTyping = true) {
    try {
      const message = {
        type: 'typing',
        chat_id: chatId,
        is_typing: isTyping,
      };
      
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const sent = this.sendWebSocketMessage(message);
        if (sent) {
          logger.debug(`⌨️ 타이핑 상태 전송 (WebSocket): ${isTyping ? '입력 중' : '중지'}`);
          return;
        }
      }
      
      logger.debug('⚠️ WebSocket 사용 불가, API로 타이핑 상태 전송');
      await apiClient.post(`/chats/${chatId}/typing`, { typing: isTyping });
      logger.debug(`✅ 타이핑 상태 전송 (API): ${isTyping ? '입력 중' : '중지'}`);
      
    } catch (error) {
      logger.warn('⚠️ 타이핑 상태 전송 실패:', error);
    }
  }
  
  async getOnlineUsers(chatId) {
    try {
      const response = await apiClient.get(`/chats/${chatId}/online`);
      const data = this.normalizeApiResponse(response, 'getOnlineUsers');
      return data || [];
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'getOnlineUsers');
      throw normalizedError;
    }
  }
  
  async getOnlineStatuses(userIds) {
    if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
      logger.debug('ℹ️ [getOnlineStatuses] ID 목록이 비어있습니다.');
      return {};
    }
    
    try {
      logger.debug(`🔍 [getOnlineStatuses] 사용자 ${userIds.length}명의 온라인 상태 조회...`, userIds);
      
      try {
        const response = await apiClient.post('/users/online-status', {
          user_ids: userIds
        });
        
        const data = this.normalizeApiResponse(response, 'getOnlineStatuses');
        
        if (!data || typeof data !== 'object') {
          logger.warn('⚠️ [getOnlineStatuses] 응답이 비정상입니다:', data);
          return {};
        }
        
        logger.debug('✅ [getOnlineStatuses] API 응답:', data);
        
        return data;
        
      } catch (apiError) {
        if (apiError.status === 404 || apiError.response?.status === 404) {
          logger.debug('💡 [getOnlineStatuses] /users/online-status 엔드포인트 없음, WebSocket으로 시도');
          
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            logger.debug('📡 [getOnlineStatuses] WebSocket으로 온라인 상태 요청');
            
            this.sendWebSocketMessage({
              type: 'request_online_status',
              user_ids: userIds
            });
            
            return {};
          } else {
            logger.warn('⚠️ [getOnlineStatuses] WebSocket도 연결되지 않음');
            return {};
          }
        }
        
        throw apiError;
      }
      
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'getOnlineStatuses');
      logger.error('❌ [getOnlineStatuses] 온라인 상태 조회 실패:', normalizedError.message);
      
      return {};
    }
  }

  async backupChat(chatId, options = {}) {
    try {
      const response = await apiClient.post(`/chats/${chatId}/backup`, options);
      logger.debug(`✅ 채팅 백업 완료: ${chatId}`);
      const data = this.normalizeApiResponse(response, 'backupChat');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'backupChat');
      throw normalizedError;
    }
  }

  async exportChatHistory(chatId, format = 'json') {
    try {
      const response = await apiClient.get(`/chats/${chatId}/export`, {
        params: { format },
        responseType: 'blob',
      });
      logger.debug(`✅ 채팅 히스토리 내보내기 완료: ${format}`);
      return response.data || response;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'exportChatHistory');
      throw normalizedError;
    }
  }

  async getRecentChats(limit = 10) {
    try {
      const response = await apiClient.get('/chats/recent', {
        params: { limit },
      });
      const data = this.normalizeApiResponse(response, 'getRecentChats');
      return Array.isArray(data) ? data : [];
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'getRecentChats');
      throw normalizedError;
    }
  }

  async toggleMessageReaction(chatId, messageId, emoji, add = true) {
    try {
      const method = add ? 'post' : 'delete';
      const response = await apiClient[method](
        `/chats/${chatId}/messages/${messageId}/reactions`,
        { emoji }
      );
      logger.debug(`✅ 메시지 반응 ${add ? '추가' : '제거'} 완료`);
      const data = this.normalizeApiResponse(response, 'toggleMessageReaction');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'toggleMessageReaction');
      throw normalizedError;
    }
  }

  async reportMessage(chatId, messageId, reason) {
    try {
      const response = await apiClient.post(
        `/chats/${chatId}/messages/${messageId}/report`,
        { reason }
      );
      logger.debug(`✅ 메시지 신고 완료: ${messageId}`);
      const data = this.normalizeApiResponse(response, 'reportMessage');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'reportMessage');
      throw normalizedError;
    }
  }
  
  async joinChatRoom(chatId) {
    try {
      const response = await apiClient.post(`/chats/${chatId}/join`);
      logger.debug(`✅ 채팅방 참여 완료: ${chatId}`);
      const data = this.normalizeApiResponse(response, 'joinChatRoom');
      return data;
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'joinChatRoom');
      throw normalizedError;
    }
  }
  
  // =================== MICE 사업 전용 기능 ===================
  
  async getChatRoomLanguages(chatId) {
    if (!this.features.translation) {
      logger.debug('ℹ️ 번역 기능이 비활성화되어 있습니다');
      return ['ko', 'en'];
    }
    
    try {
      const response = await apiClient.get(`/chats/${chatId}/languages`);
      const data = this.normalizeApiResponse(response, 'getChatRoomLanguages');
      return data?.languages || ['ko', 'en'];
    } catch (error) {
      if (error.status === 404 || error.response?.status === 404) {
        logger.debug('ℹ️ 채팅방 언어 목록 엔드포인트 준비 중 (기본값 사용)');
      } else {
        logger.error('❌ 채팅방 언어 목록 조회 실패:', error);
      }
      return ['ko', 'en'];
    }
  }
  
  async createEventChatRoom(eventData) {
    try {
      const {
        eventId,
        eventName,
        eventType,
        maxParticipants = 500,
        languages = ['ko', 'en'],
        startDate,
        endDate,
        description
      } = eventData;
      
      logger.debug(`🎪 MICE 행사 채팅방 생성: ${eventName}`);
      
      const chatRoomData = {
        room_name: `📅 ${eventName}`,
        room_type: 'event',
        event_id: eventId,
        event_type: eventType,
        max_participants: maxParticipants,
        supported_languages: languages,
        metadata: {
          eventType,
          startDate,
          endDate,
          description,
          autoTranslate: this.features.translation,
          participantCount: 0
        }
      };
      
      const response = await apiClient.post('/chats', chatRoomData);
      const data = this.normalizeApiResponse(response, 'createEventChatRoom');
      
      if (this.features.translation) {
        if (languages.includes('en')) {
          await this.sendSystemMessage(data.id, `Welcome to ${eventName}!`, 'en');
        }
        if (languages.includes('zh')) {
          await this.sendSystemMessage(data.id, `欢迎来到 ${eventName}!`, 'zh');
        }
        if (languages.includes('ja')) {
          await this.sendSystemMessage(data.id, `${eventName}へようこそ!`, 'ja');
        }
      }
      
      logger.debug(`✅ MICE 행사 채팅방 생성 완료: ${data.id}`);
      return data;
      
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'createEventChatRoom');
      throw normalizedError;
    }
  }
  
  async sendSystemMessage(chatId, content, language = 'ko') {
    try {
      return await this.sendMessage(chatId, {
        type: 'system',
        content,
        language,
        isSystem: true,
        metadata: {
          timestamp: new Date().toISOString(),
          priority: 'normal'
        }
      });
    } catch (error) {
      logger.error('❌ 시스템 메시지 전송 실패:', error);
      throw error;
    }
  }
  
  async broadcastToLargeGroup(chatId, message, languages = ['ko', 'en', 'zh', 'ja']) {
    try {
      logger.debug(`📢 대규모 브로드캐스트 시작: ${languages.length}개 언어`);
      
      const results = {};
      
      for (const lang of languages) {
        let translatedMessage = message;
        
        if (this.features.translation && lang !== this.preferredLanguage) {
          translatedMessage = await this.translateMessage(message, lang, this.preferredLanguage);
        }
        
        const sentMessage = await this.sendMessage(chatId, {
          type: 'broadcast',
          content: translatedMessage,
          language: lang,
          metadata: {
            originalLanguage: this.preferredLanguage,
            isMultilingual: this.features.translation,
            priority: 'high'
          }
        });
        
        results[lang] = sentMessage;
      }
      
      logger.debug(`✅ 브로드캐스트 완료: ${languages.length}개 언어`);
      return results;
      
    } catch (error) {
      logger.error('❌ 브로드캐스트 실패:', error);
      throw error;
    }
  }
  
  async sendScheduleMessage(chatId, scheduleData) {
    try {
      const {
        title,
        startTime,
        endTime,
        location,
        description,
        attendees = [],
        reminder = true
      } = scheduleData;
      
      logger.debug(`📅 일정 메시지 전송: ${title}`);
      
      const scheduleMessage = {
        type: 'schedule',
        content: title,
        language: this.preferredLanguage,
        metadata: {
          startTime,
          endTime,
          location,
          description,
          attendees,
          reminder,
          createdBy: this.userId
        }
      };
      
      const message = await this.sendMessage(chatId, scheduleMessage);
      
      if (attendees.length > 0) {
        this.sendWebSocketMessage({
          type: 'schedule_notification',
          chat_id: chatId,
          schedule: scheduleData,
          attendee_ids: attendees.map(a => a.id)
        });
      }
      
      logger.debug(`✅ 일정 메시지 전송 완료`);
      return message;
      
    } catch (error) {
      logger.error('❌ 일정 메시지 전송 실패:', error);
      throw error;
    }
  }
  
  async updateChatRoomLanguages(chatId, languages) {
    try {
      const response = await apiClient.put(`/chats/${chatId}`, {
        supported_languages: languages
      });
      
      logger.debug(`🌍 채팅방 언어 설정 업데이트: ${languages.join(', ')}`);
      const data = this.normalizeApiResponse(response, 'updateChatRoomLanguages');
      return data;
      
    } catch (error) {
      const normalizedError = this.normalizeApiError(error, 'updateChatRoomLanguages');
      throw normalizedError;
    }
  }
  
  async getTranslationStats(chatId) {
    try {
      const response = await apiClient.get(`/chats/${chatId}/translation-stats`);
      const data = this.normalizeApiResponse(response, 'getTranslationStats');
      return data;
    } catch (error) {
      logger.error('❌ 번역 통계 조회 실패:', error);
      return {
        totalTranslations: 0,
        languageDistribution: {},
        mostUsedLanguages: []
      };
    }
  }
}

const chatsService = new ChatsService();
export default chatsService;
